'use client';
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Box, Divider, Paper, Menu, MenuItem, Button, Typography, IconButton, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Papa from 'papaparse';
import ExcelJS from 'exceljs';
import { useLanguage } from '../utils/i18n';

const DataTableV2 = ({
  title = '',
  rows = [],
  columns = [],
  columnGroupingModel = [],
  loading = false,
  pageSizeOptions = [10, 25, 50, 100],
  paginationMode = 'server',
  rowCount,
  paginationModel,
  onPaginationModelChange,
  searchQuery = '',
  onSearchChange = () => {},
  onSearch = null,
  searchLabel = 'Search',
  searchFieldOptions = null,
  selectedSearchField = null,
  onSearchFieldChange = null,
  hideExportButton = false,
  extraButtons = null,
  onFetchAllForExport = null
}) => {
  const { t } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const openExport = Boolean(anchorEl);
  const tableRef = useRef(null);

  // =========================
  // Search Field Logic
  // =========================
  const [internalSearchField, setInternalSearchField] = useState(
    selectedSearchField ?? (Array.isArray(searchFieldOptions) && searchFieldOptions.length ? searchFieldOptions[0].value : null)
  );

  useEffect(() => {
    if (selectedSearchField != null) setInternalSearchField(selectedSearchField);
  }, [selectedSearchField]);

  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const openFieldMenu = Boolean(fieldMenuAnchor);
  const handleFieldButtonClick = (e) => setFieldMenuAnchor(e.currentTarget);
  const handleFieldMenuClose = () => setFieldMenuAnchor(null);
  const handleFieldSelect = (val) => {
    handleFieldMenuClose();
    if (onSearchFieldChange) onSearchFieldChange(val);
    if (selectedSearchField == null) setInternalSearchField(val);
  };

  const handleSearchInputChange = (e) => {
    const val = e?.target?.value ?? '';
    const field = internalSearchField;
    if (typeof onSearch === 'function') {
      onSearch({ field, value: val, originalEvent: e });
      return;
    }
    if (typeof onSearchChange === 'function') {
      onSearchChange(e);
    }
  };

  const currentFieldLabel =
    (Array.isArray(searchFieldOptions) && searchFieldOptions.find((s) => String(s.value) === String(internalSearchField))?.label) ||
    searchLabel ||
    t('general.search', 'Search');

  // =========================
  // Dynamic Columns (Auto "No")
  // =========================
  const dynamicColumns = useMemo(() => {
    if ((!columns || columns.length === 0) && rows && rows.length > 0) {
      const sample = rows[0];
      const autoCols = Object.keys(sample).map((key) => ({
        field: key,
        headerName: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) || key,
        flex: 1,
        renderCell: (params) => params.value ?? '-'
      }));
      return [
        {
          field: '__no__',
          headerName: t('dataTable.no', 'No'),
          width: 50,
          sortable: false,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        },
        ...autoCols
      ];
    }

    if (columns && columns.length > 0) {
      return [
        {
          field: '__no__',
          headerName: t('dataTable.no', 'No'),
          width: 50,
          sortable: false,
          align: 'center',
          headerAlign: 'center',
          renderCell: (params) => params.api.getRowIndexRelativeToVisibleRows(params.id) + 1
        },
        ...columns
      ];
    }

    return [];
  }, [columns, rows, t]);

  // =========================
  // Export Logic
  // =========================
  const getFormattedDate = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}${month}${day}`;
  };

  const getCellValue = (row, col, index) => {
    try {
      if (col.field === '__no__') return index + 1;

      if (typeof col.valueGetter === 'function') {
        if (col.valueGetter.length > 1) {
          return col.valueGetter(row[col.field], row, col, null) ?? '';
        } else {
          return col.valueGetter({ row, value: row[col.field], colDef: col, id: index }) ?? '';
        }
      }

      if (col.field === 'company' || col.field === 'company_id') {
        if (row.join && row.join.company_name) return row.join.company_name;
        if (row.company_name) return row.company_name;
      }

      return row[col.field] ?? '';
    } catch {
      return row[col.field] ?? '';
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const exportToCSV = async () => {
    setIsExporting(true);
    try {
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);

      const formattedData = dataToExport.map((row, index) => {
        const rowValues = {};
        exportCols.forEach((col) => {
          let val = getCellValue(row, col, index);
          if (Array.isArray(val)) val = val.join(', ');
          rowValues[col.headerName || col.field] = val;
        });
        return rowValues;
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(title || 'Sheet1');
      worksheet.columns = exportCols.map((col) => ({
        header: col.headerName || col.field,
        key: col.field,
        width: col.width ? Math.min(Math.max(col.width / 8, 10), 60) : 30
      }));

      let currentExcelRow = 2;
      dataToExport.forEach((row, index) => {
        const rowValues = {};
        let maxSubRows = 1;

        exportCols.forEach((col) => {
          const val = getCellValue(row, col, index);
          if (Array.isArray(val)) {
            if (val.length > maxSubRows) maxSubRows = val.length;
          }
          rowValues[col.field] = val;
        });

        for (let i = 0; i < maxSubRows; i++) {
          const subRowValues = {};
          exportCols.forEach((col) => {
            const val = rowValues[col.field];
            if (Array.isArray(val)) {
              subRowValues[col.field] = val[i] !== undefined ? val[i] : '';
            } else {
              subRowValues[col.field] = i === 0 ? val : '';
            }
          });
          worksheet.addRow(subRowValues);
        }

        if (maxSubRows > 1) {
          exportCols.forEach((col, colIdx) => {
            const val = rowValues[col.field];
            if (!Array.isArray(val)) {
              const colLetter = worksheet.getColumn(colIdx + 1).letter;
              worksheet.mergeCells(`${colLetter}${currentExcelRow}:${colLetter}${currentExcelRow + maxSubRows - 1}`);
              worksheet.getCell(`${colLetter}${currentExcelRow}`).alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            }
          });
        }
        currentExcelRow += maxSubRows;
      });

      const headerRow = worksheet.getRow(1);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF1976D2' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };

      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
      });

      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const exportToPDF = async () => {
    setIsExporting(true);
    try {
      const dataToExport = typeof onFetchAllForExport === 'function' ? await onFetchAllForExport() : rows;
      const doc = new jsPDF('landscape');

      const exportCols = dynamicColumns.filter((col) => col.field !== 'actions' && col.field !== 'action' && !col.excludeFromExport);
      const tableColumn = exportCols.map((col) => col.headerName || col.field);

      const tableRows = dataToExport.map((row, index) => {
        return exportCols.map((col) => {
          let val = getCellValue(row, col, index);
          if (Array.isArray(val)) val = val.join('\n');
          return val !== null && val !== undefined ? String(val) : '-';
        });
      });

      if (title) {
        doc.setFontSize(16);
        doc.text(title, 14, 15);
      }

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: title ? 20 : 14,
        theme: 'grid',
        styles: { fontSize: 8 },
        headStyles: { fillColor: [25, 118, 210] }
      });

      doc.save(`${(title || 'export').replace(/\s+/g, '-').toLowerCase()}-${getFormattedDate()}.pdf`);
    } catch (err) {
      console.error('Failed to export PDF:', err);
    } finally {
      setIsExporting(false);
      handleExportClose();
    }
  };

  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const normalizedExtraButtons = React.Children.toArray(extraButtons);

  return (
    <Paper
      sx={{
        height: 'calc(100vh - 100px)',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        p: 2
      }}
    >
      {/* Header Area */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{title}</Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {normalizedExtraButtons.length > 0 &&
            normalizedExtraButtons.map((btn, idx) => <React.Fragment key={`extra-btn-${idx}`}>{btn}</React.Fragment>)}

          {/* Search Box */}
          {Array.isArray(searchFieldOptions) && searchFieldOptions.length > 0 ? (
            <TextField
              label={currentFieldLabel}
              variant="outlined"
              size="medium"
              value={searchQuery}
              onChange={handleSearchInputChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0 }}>
                    <IconButton size="small" aria-label="search" disableRipple>
                      <SearchIcon />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ ml: 1, width: '2px', height: 24, bgcolor: 'grey.300' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={handleFieldButtonClick}
                        aria-label="open search fields"
                        sx={{ width: 28, height: 28, p: 0 }}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                )
              }}
              sx={{ maxWidth: 220 }}
            />
          ) : (
            <TextField
              label={searchLabel}
              variant="outlined"
              size="medium"
              value={searchQuery}
              onChange={handleSearchInputChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
              sx={{ maxWidth: 220 }}
            />
          )}

          <Menu anchorEl={fieldMenuAnchor} open={openFieldMenu} onClose={handleFieldMenuClose}>
            {Array.isArray(searchFieldOptions) &&
              searchFieldOptions.map((opt) => (
                <MenuItem
                  key={String(opt.value)}
                  selected={String(opt.value) === String(internalSearchField)}
                  onClick={() => handleFieldSelect(opt.value)}
                >
                  {opt.label}
                </MenuItem>
              ))}
          </Menu>

          {/* Export Button */}
          {!hideExportButton && (
            <>
              <Button
                startIcon={<DownloadIcon />}
                onClick={handleExportClick}
                aria-controls={openExport ? 'export-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={openExport ? 'true' : undefined}
                variant="outlined"
                sx={{ height: 40 }}
                disabled={isExporting || (typeof onFetchAllForExport === 'function' && !onFetchAllForExport)}
              >
                {isExporting ? t('general.exporting', 'Exporting...') : t('general.export', 'Export')}
              </Button>

              <Menu id="export-menu" anchorEl={anchorEl} open={openExport} onClose={handleExportClose}>
                <MenuItem onClick={exportToCSV}>{t('table.export.csv', 'Export to CSV')}</MenuItem>
                <MenuItem onClick={exportToExcel}>{t('table.export.excel', 'Export to Excel')}</MenuItem>
                <MenuItem onClick={exportToPDF}>{t('table.export.pdf', 'Export to PDF')}</MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Box>

      {/* DataGrid Area */}
      <Box sx={{ flexGrow: 1, overflow: 'hidden', width: '100%' }} ref={tableRef}>
        <DataGrid
          rows={rows || []}
          columns={dynamicColumns}
          columnGroupingModel={columnGroupingModel}
          loading={!!loading}
          pageSizeOptions={pageSizeOptions}
          paginationMode={paginationMode}
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          getRowId={(row) => row.id ?? row._id ?? JSON.stringify(row)}
          density="compact"
          rowHeight={60}
          headerHeight={50}
          disableRowSelectionOnClick
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#485D9A !important',
              color: '#ffffff !important'
            },

            '& .MuiDataGrid-columnHeader': {
              backgroundColor: '#485D9A !important',
              color: '#ffffff !important'
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600
            },
            // '& .MuiDataGrid-iconSeparator': {
            //   color: '#485D9A'
            // },
            '& .MuiDataGrid-menuIcon .MuiSvgIcon-root': {
              color: '#485D9A'
            },
            '& .MuiDataGrid-sortIcon': {
              color: '#485D9A'
            },
            '& .MuiDataGrid-cell': {
              borderColor: (theme) => theme.palette.divider
            },
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => theme.palette.divider,
            fontSize: 14
          }}
        />
      </Box>
    </Paper>
  );
};

export default DataTableV2;
